import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { RateType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import { Chip } from '~components/Chip/Chip';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import { Load } from '~hooks/useLoad/models';
import { getRateTypeLabel } from '~pages/Settings/RatesManagement/rateUtils';
import { Nullable } from '~types/Nullable';

interface CandidateRate {
  label: string;
  type: Nullable<RateType>;
  value: Nullable<number>;
}

type VisibleRate = CandidateRate & { value: number };

export const formatLoadRate = (
  currencyFormatter: (value: number | string) => string,
  value: number,
  type: Nullable<RateType>,
) => {
  const formattedValue =
    type === RateType.RATE_COMMISSION ? `${value}%` : currencyFormatter(value);
  const typeLabel = type ? getRateTypeLabel(type) : '';

  return typeLabel ? `${formattedValue} · ${typeLabel}` : formattedValue;
};

/**
 * Divergence indicator for Loads whose pricing differs from their hop
 * (`rate_inherited === false` under the `load_level_pricing` flag). Renders a
 * small "Custom rate" chip plus a compact formatted rate line for the sides
 * the viewer is allowed to see; the tooltip lists every visible side.
 */
export default function LoadCustomRateIndicator({ load }: { load: Load }) {
  const theme = useTheme();
  const { currencyFormatter } = useCompanyCurrency();

  const candidateRates: CandidateRate[] = [
    {
      label: t('loads.pricing.customer'),
      type: load.customerRateType,
      value: load.customerRateValue,
    },
    {
      label: t('loads.pricing.vendor'),
      type: load.vendorRateType,
      value: load.vendorRateValue,
    },
    {
      label: t('loads.pricing.driver'),
      type: load.driverRateType,
      value: load.driverRateValue,
    },
  ];
  const visibleRates = candidateRates.filter(
    (rate): rate is VisibleRate => rate.value !== null && rate.value !== undefined,
  );

  const tooltipTitle = visibleRates.length ? (
    <>
      {visibleRates.map((rate) => (
        <Typography key={rate.label} variant="body2">
          {`${rate.label}: ${formatLoadRate(currencyFormatter, rate.value, rate.type)}`}
        </Typography>
      ))}
    </>
  ) : (
    t('loads.pricing.custom_rate_tooltip')
  );

  return (
    <Tooltip title={tooltipTitle}>
      <Box
        sx={{
          alignItems: 'flex-end',
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
        }}
      >
        <Chip color="info" label={t('loads.pricing.custom_rate')} hideDropdownArrow />

        {visibleRates.length > 0 && (
          <Typography
            variant="body2"
            sx={{ color: theme.brandV2.colors.treadGray2, fontSize: '10px' }}
          >
            {formatLoadRate(
              currencyFormatter,
              visibleRates[0].value,
              visibleRates[0].type,
            )}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}
